import { useState, useEffect, useMemo } from 'react';
import { Placeholder, Icon, ProductCard } from './Shared';
import { useProduct, useProducts } from '../../lib/useShopify';
import { useCart } from '../../lib/useCart';
import { formatMoney } from '../../lib/shopify';

const ENJOY_WAYS = [
  { step: '01', title: 'Warm Milk', desc: 'Steep for 2 minutes. Grains soften, fruit blooms.', icon: 'warmmilk' },
  { step: '02', title: 'Cold Milk', desc: 'Eat right away. Maximum crunch, maximum bite.', icon: 'coldmilk' },
  { step: '03', title: 'With Yogurt', desc: 'Fold through thick curd. Let it sit five minutes.', icon: 'yogurt' },
  { step: '04', title: 'Smoothie Bowl', desc: 'Scatter on top of banana & berry blends.', icon: 'smoothie' },
];

const HAWELO_STORY = [
  { tag: 'HA', label: 'Healthy Habit', color: 'high', body: 'Wellness is not a week-long reset. It is a small honest choice, made the same way every morning.' },
  { tag: 'WE', label: 'Wellness Everyday', color: 'mid', body: 'Every batch is cooked, dried, or pressed in small runs so nothing waits long enough to lose itself.' },
  { tag: 'LO', label: 'Love for Real Food', color: 'low', body: 'We source from farms we have walked, from people we have met.' },
];

const BOWL_BREAKDOWN = [
  { pct: 50, label: 'Millets', color: '#D4A843', icon: 'millet' },
  { pct: 30, label: 'Berries', color: '#E8546A', icon: 'berries' },
  { pct: 20, label: 'Nuts & Seeds', color: '#4CAF50', icon: 'nuts' },
  { pct: 10, label: 'Love', color: '#F5F0E8', icon: 'heart' },
];

export function ProductPage({ handle, onOpen, goCollections, openCart }) {
  const { product, loading } = useProduct(handle);
  const { products: others } = useProducts(6);
  const { addLine } = useCart();
  const [qty, setQty] = useState(1);
  const [variantIdx, setVariantIdx] = useState(0);
  const [thumbIdx, setThumbIdx] = useState(0);
  const [openRow, setOpenRow] = useState(0);

  useEffect(() => {
    setQty(1); setVariantIdx(0); setThumbIdx(0); setOpenRow(0);
    window.scrollTo({ top: 0 });
  }, [handle]);

  const selectedVariant = product?.variants?.[variantIdx];
  const linePrice = selectedVariant?.price?.amount
    ? Number(selectedVariant.price.amount) * qty
    : (product?.price || 0) * qty;

  const related = useMemo(
    () => others.filter((p) => p.handle !== handle).slice(0, 3),
    [others, handle]
  );

  if (loading) {
    return (
      <div className="page-view" data-testid="product-loading">
        <div className="wrap pd-wrap">
          <div className="pd-hero">
            <div className="pd-gallery"><div className="pd-main-img"><Placeholder label="loading…" /></div></div>
            <div className="pd-info"><h1 className="pd-title">Loading…</h1></div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="page-view">
        <div className="wrap pd-wrap">
          <h1 className="pd-title">Not found.</h1>
          <button className="imp-btn" onClick={goCollections}>Back to shop</button>
        </div>
      </div>
    );
  }

  const handleAdd = async () => {
    if (!selectedVariant?.id) return;
    await addLine(selectedVariant.id, qty);
    openCart && openCart();
  };

  return (
    <div className="page-view" data-testid="product-page">
      <div className="wrap pd-wrap">
        <div className="pd-breadcrumb">
          <a onClick={() => onOpen && window.scrollTo({ top: 0 })}>Home</a><span>/</span>
          <a onClick={goCollections}>{product.productType || 'Shop'}</a><span>/</span>
          <span style={{ color: '#1E2B8C', opacity: 1 }}>{product.title}</span>
        </div>

        <section className="pd-hero">
          <div className="pd-gallery">
            <div className="pd-main-img">
              {product.images[thumbIdx]?.url ? (
                <img
                  src={product.images[thumbIdx].url}
                  alt={product.images[thumbIdx].altText || product.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Placeholder label={`${product.title}\nview ${thumbIdx + 1}`} />
              )}
              <div className="natural-badge">110% Natural</div>
            </div>
            {product.images.length > 1 && (
              <div className="pd-thumbs">
                {product.images.slice(0, 4).map((img, i) => (
                  <div
                    key={i}
                    className={`pd-thumb ${thumbIdx === i ? 'active' : ''}`}
                    onClick={() => setThumbIdx(i)}
                    data-testid={`pd-thumb-${i}`}
                  >
                    <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="pd-info">
            <span className="imp-tag">{product.tags?.[0] || 'slow-cooked, never over-processed'}</span>
            <h1 className="pd-title">{product.title}</h1>
            {product.description && (
              <p className="pd-subtitle">{product.description}</p>
            )}
            <div className="pd-price-row">
              <span className="pd-price">{formatMoney(product.price, product.currency)}</span>
              <span className="pd-unit">· 400 g pack · serves 10</span>
            </div>

            {product.variants.length > 1 && (
              <>
                <div className="pd-variant-label">Option</div>
                <div className="pd-variants">
                  {product.variants.map((v, i) => (
                    <button
                      key={v.id}
                      className={`pd-variant ${variantIdx === i ? 'active' : ''}`}
                      onClick={() => setVariantIdx(i)}
                      data-testid={`variant-${i}`}
                    >{v.title}</button>
                  ))}
                </div>
              </>
            )}

            <div className="pd-variant-label">Quantity</div>
            <div className="pd-cta-row">
              <div className="pd-qty">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="pd-qty-dec">−</button>
                <input value={qty} readOnly data-testid="pd-qty-val" />
                <button onClick={() => setQty((q) => q + 1)} data-testid="pd-qty-inc">+</button>
              </div>
              <button
                className="imp-btn"
                onClick={handleAdd}
                data-testid="pd-add-to-cart"
                disabled={!product.availableForSale}
              >
                {product.availableForSale
                  ? `Add to Cart · ${formatMoney(linePrice, product.currency)}`
                  : 'Sold Out'}
              </button>
              <span className="imp-tag green">110% Natural</span>
            </div>

            <div className="pd-trust">
              <div className="pd-trust-item">
                <strong>No refined sugar</strong>
                <span>Sweetened only by what grew that way.</span>
              </div>
              <div className="pd-trust-item">
                <strong>Small-batch</strong>
                <span>Cooked in lots of 40 kg, never larger.</span>
              </div>
              <div className="pd-trust-item">
                <strong>Ships in 48h</strong>
                <span>Free above ₹499 · Pan-India.</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="section-inside">
        <div className="wrap">
          <div className="inside-grid">
            <div>
              <div className="inside-eyebrow">§ 01 — What's inside this pack</div>
              <h2 className="inside-head">The ingredient story,<br />told long-form.</h2>
            </div>
            <div className="inside-body">
              <p>{product.description || 'A slow-toasted blend of heirloom grains, fruits, and nuts — folded together in small batches.'}</p>
              <p>What you get is texture you can hear. The crunch of real grain. The chew of fruit that still remembers the vine.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-bowl-pd">
        <div className="wrap">
          <div className="imp-callout bowl-callout">
            <div className="bowl-copy">
              <div className="bowl-eyebrow">§ 02 — What goes into your bowl</div>
              <h3>Ratios we keep.</h3>
              <p>This is the exact breakdown inside {product.title}. Written on the pack. Honoured in every batch.</p>
            </div>
            <div className="bowl-chart">
              {BOWL_BREAKDOWN.map((b) => (
                <div className="bowl-item" key={b.label}>
                  <div className="icon"><Icon kind={b.icon} size={52} /></div>
                  <div className="pct" style={{ color: b.color }}>{b.pct}%</div>
                  <div className="label">{b.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-enjoy">
        <div className="wrap">
          <div className="section-head" style={{ marginBottom: 40 }}>
            <h2>§ 03 — How to enjoy.</h2>
            <p>Four ways we eat it at the kitchen. Pick your morning mood.</p>
          </div>
          <div className="enjoy-grid">
            {ENJOY_WAYS.map((w) => (
              <div className="enjoy-card" key={w.step}>
                <div className="step-num">— {w.step}</div>
                <div className="icon"><Icon kind={w.icon} size={60} color="#1E2B8C" /></div>
                <h4>{w.title}</h4>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="honest-wrap">
        <div className="wrap">
          <div className="section-head" style={{ marginBottom: 32 }}>
            <h2>§ 04 — Honest food statement.</h2>
            <p>Three promises we put in writing.</p>
          </div>
          <div className="honest-grid">
            <div className="honest-box"><div className="x">No refined sugar.</div><p>If it's sweet, it was born sweet.</p></div>
            <div className="honest-box navy"><div className="x">No artificial flavours.</div><p>If it tastes like cinnamon, it is cinnamon.</p></div>
            <div className="honest-box green"><div className="x">No unnecessary fillers.</div><p>No maltodextrin, no anti-caking agents.</p></div>
          </div>
        </div>
      </section>

      <section className="section-hawelo">
        <div className="wrap">
          <h2 className="hawelo-head">HA · WE · LO.<br />What the name carries.</h2>
          <div className="hawelo-accordion">
            {HAWELO_STORY.map((row, i) => (
              <div key={i} className={`hawelo-row ${openRow === i ? 'open' : ''}`}>
                <div
                  className="hawelo-head-row"
                  onClick={() => setOpenRow(openRow === i ? -1 : i)}
                  data-testid={`hawelo-row-${i}`}
                >
                  <div className={`hawelo-tag ${row.color}`}>{row.tag}</div>
                  <div className="hawelo-label">{row.label}</div>
                  <div className="hawelo-toggle">+</div>
                </div>
                <div className="hawelo-body"><p>{row.body}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="related-section">
          <div className="wrap">
            <div className="section-head" style={{ marginBottom: 40 }}>
              <h2>You may also like.</h2>
              <p>Other pantry honest-ies from the same kitchen.</p>
            </div>
            <div className="product-rail">
              {related.map((p) => <ProductCard key={p.id} product={p} onAdd={addLine} onOpen={onOpen} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export function CollectionsPage({ onOpen }) {
  const { products, loading } = useProducts(24);
  const { addLine } = useCart();
  const [filter, setFilter] = useState('All');

  const types = useMemo(() => {
    const s = new Set();
    products.forEach((p) => {
      if (p.productType) s.add(p.productType);
      else if (p.tags && p.tags.length) p.tags.slice(0, 2).forEach((t) => s.add(t));
    });
    return ['All', ...Array.from(s)];
  }, [products]);

  const filtered = filter === 'All'
    ? products
    : products.filter((p) => p.productType === filter || (p.tags || []).includes(filter));

  return (
    <div className="page-view" data-testid="collections-page">
      <div className="wrap">
        <div className="coll-head">
          <h1>The whole<br />honest shelf.</h1>
          <p>Six products. Six recipes. One standard. Filter by what your morning looks like.</p>
        </div>
        <div className="coll-filterbar">
          <span className="filter-label">Filter</span>
          {types.map((c) => (
            <button
              key={c}
              className={`filter-pill ${filter === c ? 'active' : ''}`}
              onClick={() => setFilter(c)}
              data-testid={`filter-${c.replace(/\s+/g, '-').toLowerCase()}`}
            >{c}</button>
          ))}
          <span className="filter-count" data-testid="filter-count">
            {filtered.length} {filtered.length === 1 ? 'product' : 'products'}
          </span>
        </div>
        {loading ? (
          <div className="coll-rail">
            {[0, 1, 2].map((i) => (
              <article className="product-card" key={i}>
                <div className="p-image"><Placeholder label="loading…" /></div>
                <h3 className="product-name">&nbsp;</h3>
              </article>
            ))}
          </div>
        ) : (
          <div className="coll-rail">
            {filtered.map((p) => <ProductCard key={p.id} product={p} onAdd={addLine} onOpen={onOpen} />)}
          </div>
        )}
      </div>
    </div>
  );
}
